/**
 * A HTML element that formats a supplied ISO timestamp into a localized date and time string.
 * 
 * The 'timestamp' attribute is required and should be an ISO 8601 formatted date string.
 * 
 * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#date-time_component_options
 * for a list of available options for each formatting attribute.
 */
class LocalizedTimestamp extends HTMLElement {
    static get observedAttributes() {
        return [
            "timestamp",
            "year",
            "month",
            "day",
            "hour",
            "minute",
            "second",
            "time",
            "weekday",
            "era",
            "date-style",
            "time-style",
            "time-zone-name",
            "time-zone",
        ];
    }

    private language: string = "en";

    constructor() {
        super();

        this.language = this.getLanguage();
        const fallback = this.getAttribute("fallback");
        if (fallback) {
            this.innerHTML = fallback;
        }
    }

    connectedCallback() {
        this.render();
    }

    attributeChangedCallback(_name, oldValue, newValue) {
        if (oldValue !== newValue) {
            this.render();
        }
    }

    dateTimeToLocalTime(dateTimeString) {
        const date = new Date(dateTimeString);
        const timeZone = this.getAttribute("time-zone") ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
        const language = this.getAttribute("language") ?? this.language;
        const options: Intl.DateTimeFormatOptions = {
            timeZone: timeZone,
        };

        [
            "era",
            "year",
            "month",
            "day",
            "weekday",
            "hour",
            "minute",
            "second",
        ].forEach((attr) => {
            this.maybeSetOption(options, attr);
        });

        this.maybeSetOption(options, "date-style", "dateStyle");
        this.maybeSetOption(options, "time-style", "timeStyle");
        this.maybeSetOption(options, "time-zone-name", "timeZoneName");

        const formatter = Intl.DateTimeFormat(language, options);
        return formatter.format(date);
    }

    maybeSetOption(options: Intl.DateTimeFormatOptions, attrName: string, optionName?: string) {
        if (typeof optionName === "undefined") {
            optionName = attrName;
        }

        if (this.getAttribute(attrName)) {
            options[optionName] = this.getAttribute(attrName);
        }
    }

    getLanguage() {
        if (navigator.languages && navigator.languages.length > 0) {
            return navigator.languages[0];
        }

        return navigator.language || (navigator as any).userLanguage;
    }

    render() {
        const oldChildValue = this.innerHTML;

        try {
            this.innerHTML = "";
            const timeStr = this.dateTimeToLocalTime(this.getAttribute("timestamp"));
            const txtNode = document.createTextNode(timeStr);
            this.appendChild(txtNode);
        } catch (error) {
            console.error(error);
            this.innerHTML = oldChildValue;
        }
    }
}

window.customElements.define("localized-timestamp", LocalizedTimestamp);
